/**
 * A playlist an administrator shared with this account, read-only: its name
 * and slides, with nothing to edit, reorder, add or delete. The owner is the
 * only one who can change it (anyone else gets a 403), so no control is offered.
 */
import { Film, ImageOff, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { formatSchedule, scheduleStatus } from './playlistSchedule';
import { SHARED_PLAYLIST_BADGE, sharedPlaylistName, type SharedPlaylist } from './sharedPlaylists';

export function SharedPlaylistBadge() {
  return (
    <Badge variant="purple" size="sm">
      {SHARED_PLAYLIST_BADGE}
    </Badge>
  );
}

export function SharedPlaylistViewer({
  playlistId,
  entry,
  onClose,
  onPlaylistsPage = false,
}: {
  playlistId: number;
  entry: SharedPlaylist | undefined;
  onClose: () => void;
  onPlaylistsPage?: boolean;
}) {
  const items = entry?.status === 'ok' ? entry.data.items : [];

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={sharedPlaylistName(entry, playlistId)}
      size="lg"
      footer={
        <div className="flex justify-end">
          <Button onClick={onClose}>Close</Button>
        </div>
      }
    >
      <div className="mb-3 flex items-center gap-2">
        <SharedPlaylistBadge />
        <span className="text-sm text-slate-500">Read-only. Only Corporate can change it.</span>
      </div>
      {!entry || entry.status === 'loading' ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : entry.status === 'unavailable' ? (
        <p className="py-8 text-center text-sm text-slate-500">
          {onPlaylistsPage
            ? 'This playlist is no longer available. It may have been deleted or unshared.'
            : 'This playlist is no longer available. Pick one of your own playlists for this screen.'}
        </p>
      ) : entry.status === 'error' ? (
        <div className="flex flex-col items-center gap-3 py-8 text-center text-sm text-slate-500">
          <p>This playlist couldn&apos;t be loaded right now.</p>
          {entry.retry && (
            <Button variant="secondary" size="sm" onClick={entry.retry}>
              Try again
            </Button>
          )}
        </div>
      ) : items.length === 0 ? (
        <p className="py-8 text-center text-sm text-slate-500">This playlist has no slides.</p>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <div key={item.id} className="flex items-center gap-3 rounded-lg border border-slate-200 p-2">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded bg-slate-100">
                {item.media?.type === 'video' ? (
                  <Film className="h-5 w-5 text-slate-400" />
                ) : item.media?.url ? (
                  <img src={item.media.url} alt="" className="h-full w-full object-cover" />
                ) : (
                  <ImageOff className="h-5 w-5 text-slate-300" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-900">
                  {item.media?.name ?? `Media #${item.mediaId}`}
                </p>
                <p className="text-xs text-slate-500">
                  {item.duration}s · {formatSchedule(item.startDate, item.endDate)}
                  {scheduleStatus(item.startDate, item.endDate) === 'expired' && ' · expired'}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
